import _ from 'lodash';
import { filterByExpiryDate } from '../../hooks/knowledge.hooks';
import { normalizedContains, removeDiacritics } from '../../utils/Utils';
import { filterByTags } from '../../utils/search.utils';
import { getContacts } from './contact.selector';
import Fuse from 'fuse.js';
import { isImprovedSearchEnabled } from './features.selector';
import { SEARCH_TYPES_KEYS, CATEGORY_TYPES } from '../../constants';
import {
  TAGS_SPECIALTY,
  TAGS_KNOWLEDGE_CONTENT_TYPE,
  TAGS_LAB_CONTENT_TYPE,
  TAGS_CHECKLIST_CONTENT_TYPE,
  TAGS_TARGET_AUDIENCE,
} from '../../constants/tags.constants';

const SHOW_SCORE = false;
const PATTERN = /[a-f\d]{24}$/i;
const isValidObjectId = id => PATTERN.test(id);
const getIdFromTerm = term => term.match(PATTERN)?.pop();

/**
 * Convert an object to a lookup object with keys set to true
 * @param {Object} obj - Object with values (for constants) or keys (for state objects)
 * @param {boolean} [useKeys=false] - If true, use object keys instead of values
 * @returns {{ [key: string]: boolean }} Lookup object
 */
const toLookup = (obj, useKeys = false) => {
  const items = useKeys ? Object.keys(obj) : Object.values(obj);
  return items.reduce((acc, value) => {
    acc[value] = true;
    return acc;
  }, {});
};

/**
 * Build valid tags object from constants and Redux state
 * @param {Object} state - Redux state
 * @returns {{
 *  specialty: { [key: string]: boolean },
 *  subspecialty: { [key: string]: boolean },
 *  contentType: { [key: string]: boolean },
 *  targetAudience: { [key: string]: boolean },
 *  labels: { [key: string]: boolean },
 * }}
 */
const buildValidTags = state => {
  const specialtyLookup = toLookup(TAGS_SPECIALTY);

  const contentTypeLookup = {
    ...toLookup(TAGS_KNOWLEDGE_CONTENT_TYPE),
    ...toLookup(TAGS_LAB_CONTENT_TYPE),
    ...toLookup(TAGS_CHECKLIST_CONTENT_TYPE),
  };

  const targetAudienceLookup = toLookup(TAGS_TARGET_AUDIENCE);

  const labelsLookup = state.labels?.list ? toLookup(state.labels.list, true) : {};

  return {
    specialty: specialtyLookup,
    subspecialty: specialtyLookup,
    contentType: contentTypeLookup,
    targetAudience: targetAudienceLookup,
    labels: labelsLookup,
  };
};

// Search for knowledge item
const searchOnKnowledgeItem = ({ title, subtitle, keywords = [] }, term) => {
  return (
    normalizedContains(title, term) ||
    normalizedContains(subtitle, term) ||
    keywords.some(key => normalizedContains(key, term))
  );
};

// Search for lab item
const searchOnLabItem = ({ title, value, keywords = [], drugClass }, term) => {
  return (
    normalizedContains(title, term) ||
    normalizedContains(value, term) ||
    keywords.some(key => normalizedContains(key, term)) ||
    normalizedContains(drugClass, term)
  );
};

// Search for admission item
const searchOnCategory = ({ title, keywords = [] }, term) => {
  return normalizedContains(title, term) || !!keywords.find(key => normalizedContains(key, term));
};

// Search for contacts
export const searchOnContactList = ({ firstName, lastName, name, email, department }, term) => {
  return (
    normalizedContains(firstName, term) ||
    normalizedContains(lastName, term) ||
    normalizedContains(name, term) ||
    normalizedContains(email, term) ||
    normalizedContains(department, term)
  );
};

/**
 * Search for term in results
 * @param {Object[]} results
 * @param {String} term
 * @param {Object} state
 * @returns {{
 *  knowledge: Object[],
 *  lab: Object[],
 *  admission: Object[],
 *  contacts: Object[],
 * }}
 */
export const innerSearch = (results = {}, term, state = {}) => {
  const termSplit = term.split(' ').slice(1);

  const departmentLang = state?.lang?.['TAGS'] || {};

  termSplit.forEach(termPart => {
    results.lab = results.lab.filter(item => searchOnLabItem(item, termPart));
    results.knowledge = results.knowledge?.filter(item => searchOnKnowledgeItem(item, termPart));
    results.admission = results.admission.filter(item => searchOnCategory(item, termPart));
    results.knowledgeCategories = results.knowledgeCategories.filter(category =>
      searchOnCategory(category, termPart),
    );
    results.labCategories = results.labCategories.filter(category =>
      searchOnCategory(category, termPart),
    );
    results.contacts = results.contacts?.filter(item =>
      searchOnContactList(
        { ...item, department: departmentLang[item?.professional?.department] },
        termPart,
      ),
    );
  });

  return results;
};

/**
 * Create a map of tags and their occurrences
 * @param {{
 *  specialty: { [key: string]: number },
 *  subspecialty: { [key: string]: number },
 *  contentType: { [key: string]: number },
 *  targetAudience: { [key: string]: number },
 *  labels: { [key: string]: number },
 * }} map
 * @param {{
 *  specialty: String,
 *  subspecialty: String,
 *  contentType: String,
 *  targetAudience: String[],
 *  labels: String[],
 * }} [tags={}]
 * @param {{
 *  specialty: { [key: string]: boolean },
 *  subspecialty: { [key: string]: boolean },
 *  contentType: { [key: string]: boolean },
 *  targetAudience: { [key: string]: boolean },
 *  labels: { [key: string]: boolean },
 * }} [validTags={}] - Valid organization tags for each property
 */
const addToMap = (map, tags = {}, validTags = {}) => {
  Object.entries(tags).forEach(([key, value]) => {
    if (!value) return;

    const incrementCount = tagKey => {
      // Validate that the tag key exists and is valid
      if (validTags[key] && !validTags[key][tagKey]) {
        return;
      }
      map[key][tagKey] = (map[key][tagKey] || 0) + 1;
    };

    Array.isArray(value) ? value.forEach(incrementCount) : incrementCount(value);
  });
};

/**
 * Old search mechanism using regex and inner search
 *
 * @param {Object} state
 * @param {String} term
 * @param {{}} filters
 * @param {{
 *  specialty: { [key: string]: boolean },
 *  subspecialty: { [key: string]: boolean },
 *  contentType: { [key: string]: boolean },
 *  targetAudience: { [key: string]: boolean },
 *  labels: { [key: string]: boolean },
 * }} [validTags={}] - Valid organization tags for filtering
 * @return {{
 *  initialResults: Object,
 *  results: Object[],
 * }}
 */
const oldSearchMechanism = (state, term, filters, validTags = {}) => {
  const { knowledge, lab, admission } = state;
  const contacts = getContacts(state);
  const termSplit = removeDiacritics(term).split(' ');
  const tagsMap = {
    specialty: {},
    subspecialty: {},
    contentType: {},
    targetAudience: {},
    labels: {},
  };
  const filtersByTags = filterByTags({ filters });

  const searchItems = (items, searchFunc) =>
    items?.filter(item => searchFunc(item, termSplit[0])) || [];

  const departmentLang = state.lang['TAGS'] || {};
  const searchContacts =
    contacts
      ?.map(contact => ({
        ...contact,
        department: departmentLang[contact?.professional?.department],
      }))
      .filter(contact => searchOnContactList(contact, termSplit[0])) || [];

  const results = {
    knowledge: searchItems(knowledge?.items, searchOnKnowledgeItem),
    lab: searchItems(lab?.items, searchOnLabItem),
    admission: searchItems(
      admission?.categories?.filter(({ type }) => type !== CATEGORY_TYPES.CATEGORY),
      searchOnCategory,
    ),
    knowledgeCategories: searchItems(knowledge?.categories, searchOnCategory),
    labCategories: searchItems(lab?.categories, searchOnCategory),
    admissionCategories: searchItems(
      admission?.categories?.filter(({ type }) => type === CATEGORY_TYPES.CATEGORY),
      searchOnCategory,
    ),
    contacts: searchContacts,
  };

  const initialResults = _.clone(results);

  const updatedResults = innerSearch(results, term, state);

  // Check if any filters are active
  const hasActiveFilters = filters && Object.values(filters).some(filterSet => filterSet?.size > 0);

  const mapTags = (resultItems, type) => {
    const filtered = resultItems.filter(type);
    const finalResults = hasActiveFilters ? filtered.filter(filtersByTags) : filtered;
    finalResults.forEach(item =>
      addToMap(tagsMap, { ...item?.tags, labels: item?.labels }, validTags),
    );
  };

  mapTags(updatedResults.knowledge, filterByExpiryDate);
  mapTags(updatedResults.lab, () => true);
  mapTags(updatedResults.admission, () => true);
  mapTags(updatedResults.knowledgeCategories, () => true);
  mapTags(updatedResults.labCategories, () => true);
  mapTags(updatedResults.admissionCategories, () => true);
  // Add contact locations to labels count
  updatedResults.contacts
    ?.filter(contact => contact?.professional?.location)
    .forEach(contact => {
      addToMap(tagsMap, { labels: contact.professional.location }, validTags);
    });

  const mapResultItems = (resultItems, typeKey) =>
    resultItems.map(item => ({ item, type: typeKey }));

  updatedResults.knowledge = mapResultItems(updatedResults.knowledge, SEARCH_TYPES_KEYS.documents);
  updatedResults.lab = mapResultItems(updatedResults.lab, SEARCH_TYPES_KEYS.compendium);
  updatedResults.admission = mapResultItems(updatedResults.admission, SEARCH_TYPES_KEYS.admission);
  updatedResults.knowledgeCategories = mapResultItems(
    updatedResults.knowledgeCategories,
    SEARCH_TYPES_KEYS.category,
  );
  updatedResults.labCategories = mapResultItems(
    updatedResults.labCategories,
    SEARCH_TYPES_KEYS.category,
  );
  updatedResults.admissionCategories = mapResultItems(
    updatedResults.admissionCategories,
    SEARCH_TYPES_KEYS.category,
  );

  updatedResults.contacts = mapResultItems(updatedResults.contacts, SEARCH_TYPES_KEYS.contacts);

  updatedResults.tagsMap = tagsMap;

  return { initialResults, results: updatedResults };
};

/**
 * Improved search mechanism using Fuse.js
 *
 * @param {Object} state
 * @param {String} term
 * @param {{}} filters
 * @param {{
 *  specialty: { [key: string]: boolean },
 *  subspecialty: { [key: string]: boolean },
 *  contentType: { [key: string]: boolean },
 *  targetAudience: { [key: string]: boolean },
 *  labels: { [key: string]: boolean },
 * }} [validTags={}] - Valid organization tags for filtering
 * @return {{
 *  initialResults: Object,
 *  results: Object[],
 * }}
 */
export const improvedSearchMechanism = (state, term, filters, validTags = {}) => {
  const departmentLang = state.lang['TAGS'] || {};
  const tagsMap = {
    specialty: {},
    subspecialty: {},
    contentType: {},
    targetAudience: {},
    labels: {},
  };
  const results = {};
  const contacts =
    getContacts(state)?.map(contact => ({
      ...contact,
      department: departmentLang[contact?.professional?.department],
    })) || [];

  const { IMPROVED_SEARCH_CONFIG = {} } = state.generalConfig;
  const createFuse = (items, config) => new Fuse(items || [], config || {});

  const knowledgeFuse = createFuse(state.knowledge?.items, IMPROVED_SEARCH_CONFIG?.KNOWLEDGE);
  const labFuse = createFuse(state.lab?.items, IMPROVED_SEARCH_CONFIG?.LAB);
  const admissionFuse = createFuse(
    state.admission?.categories?.filter(item => item?.type !== CATEGORY_TYPES.CATEGORY),
    IMPROVED_SEARCH_CONFIG?.CATEGORY,
  );
  const knowledgeCategoriesFuse = createFuse(
    state.knowledge?.categories,
    IMPROVED_SEARCH_CONFIG?.CATEGORY,
  );
  const labCategoriesFuse = createFuse(state.lab?.categories, IMPROVED_SEARCH_CONFIG?.CATEGORY);
  const admissionCategoriesFuse = createFuse(
    state.admission?.categories?.filter(item => item?.type === CATEGORY_TYPES.CATEGORY),
    IMPROVED_SEARCH_CONFIG?.CATEGORY,
  );
  const contactsFuse = createFuse(contacts, IMPROVED_SEARCH_CONFIG?.CONTACTS);

  const filtersByTags = filterByTags({ filters });
  const convertScoreToPercentage = score => (score * 100).toFixed(3);

  const createSearchResults = (fuse, typeKey, propName = 'title') => {
    if (isValidObjectId(term)) {
      // Perform exact match
      return fuse._docs
        .filter(item => item.id === getIdFromTerm(term))
        .map(item => ({
          item,
          type: typeKey,
          score: 0, // 0 is best possible score, perfect match
        }));
    } else {
      // Perform fuzzy search
      return fuse.search(term).map(({ item, score }) => ({
        item: SHOW_SCORE
          ? { ...item, [propName]: `${item[propName]} (${convertScoreToPercentage(score)})` }
          : item,
        type: typeKey,
        score,
      }));
    }
  };

  results.knowledge = createSearchResults(knowledgeFuse, SEARCH_TYPES_KEYS.documents);
  results.lab = createSearchResults(labFuse, SEARCH_TYPES_KEYS.compendium);
  results.admission = createSearchResults(admissionFuse, SEARCH_TYPES_KEYS.admission);
  results.knowledgeCategories = createSearchResults(
    knowledgeCategoriesFuse,
    SEARCH_TYPES_KEYS.category,
  );
  results.labCategories = createSearchResults(labCategoriesFuse, SEARCH_TYPES_KEYS.category);
  results.admissionCategories = createSearchResults(
    admissionCategoriesFuse,
    SEARCH_TYPES_KEYS.category,
  );
  results.contacts = createSearchResults(contactsFuse, SEARCH_TYPES_KEYS.contacts, 'firstName');

  const initialResults = _.clone(results);

  const hasActiveFilters = filters && Object.values(filters).some(filterSet => filterSet?.size > 0);

  const mapTags = (resultItems, doFilter) => {
    const filtered = resultItems.filter(({ item }) => doFilter(item));
    const finalResults = hasActiveFilters
      ? filtered.filter(({ item }) => filtersByTags(item))
      : filtered;
    finalResults.forEach(({ item }) =>
      addToMap(tagsMap, { ...item?.tags, labels: item?.labels }, validTags),
    );
  };

  mapTags(results.knowledge, filterByExpiryDate);
  mapTags(results.lab, () => true);
  mapTags(results.admission, () => true);
  mapTags(results.knowledgeCategories, () => true);
  mapTags(results.labCategories, () => true);
  mapTags(results.admissionCategories, () => true);

  // Add contact locations to labels count
  results.contacts
    ?.filter(({ item }) => item?.professional?.location)
    .forEach(({ item }) => {
      addToMap(tagsMap, { labels: item.professional.location }, validTags);
    });

  results.tagsMap = tagsMap;

  return { initialResults, results };
};

/**
 * Search for term in knowledge, lab, admission, and contacts
 * @param {Object} state - Redux state
 * @param {String} term - Search term
 * @param {Object} filters - Search filters
 * @returns {{
 * initialResults: {
 *  knowledge: Object[],
 *  lab: Object[],
 *  admission: Object[],
 *  knowledgeCategories: Object[],
 *  labCategories: Object[],
 *  admissionCategories: Object[],
 *  contacts: Object[],
 * },
 * results: {
 *  knowledge: Object[],
 *  lab: Object[],
 *  admission: Object[],
 *  knowledgeCategories: Object[],
 *  labCategories: Object[],
 *  admissionCategories: Object[],
 *  contacts: Object[],
 *  tagsMap: {
 *    specialty: { [key: string]: number },
 *    subspecialty: { [key: string]: number },
 *    contentType: { [key: string]: number },
 *    targetAudience: { [key: string]: number },
 *    labels: { [key: string]: number },
 *  },
 *  extendedSearch?: {
 *    knowledge: Object[],
 *    lab: Object[],
 *    admission: Object[],
 *    knowledgeCategories: Object[],
 *    labCategories: Object[],
 *    admissionCategories: Object[],
 *    contacts: Object[],
 *    tagsMap: {
 *      specialty: { [key: string]: number },
 *      subspecialty: { [key: string]: number },
 *      contentType: { [key: string]: number },
 *      targetAudience: { [key: string]: number },
 *      labels: { [key: string]: number },
 *    },
 *  },
 * }
 * }}
 */
export const search = (state, term, filters) => {
  const validTags = buildValidTags(state);

  const isImprovedSearch = isImprovedSearchEnabled(state);
  const search = oldSearchMechanism(state, term, filters, validTags);

  if (isImprovedSearch) {
    const improvedSearch = improvedSearchMechanism(state, term, filters, validTags);
    const { results } = search;
    const { results: improvedResults } = improvedSearch;

    results.extendedSearch = {
      ...improvedResults,
      knowledge: _.differenceBy(improvedResults.knowledge, results.knowledge, 'item.id'),
      lab: _.differenceBy(improvedResults.lab, results.lab, 'item.id'),
      admission: _.differenceBy(improvedResults.admission, results.admission, 'item.id'),
      knowledgeCategories: _.differenceBy(
        improvedResults.knowledgeCategories,
        results.knowledgeCategories,
        'item.id',
      ),
      labCategories: _.differenceBy(
        improvedResults.labCategories,
        results.labCategories,
        'item.id',
      ),
      admissionCategories: _.differenceBy(
        improvedResults.admissionCategories,
        results.admissionCategories,
        'item.id',
      ),
      contacts: _.differenceBy(improvedResults.contacts, results.contacts, 'item.id'),
    };

    results.tagsMap = _.mergeWith(results.tagsMap, improvedResults.tagsMap, (obj, src) =>
      _.isNumber(obj) ? src + Math.abs(obj - src) : undefined,
    );
  }

  return search;
};

export const isOnSearch = ({ search }) => {
  return search.isOnSearch;
};

export const isSearchFailed = ({ search }) => Boolean(search?.hasError);
